/**
 * Shared facts for the Legends of Tasmania signature tour page.
 *
 * Unlike every other tour, Legends is rendered by a hand-built page
 * (src/routes/tours/legends-of-tasmania-golf-history-and-flavour-4-days-4-nights)
 * rather than the data-driven /tours/[tourName] route. The itinerary still comes
 * from $lib/utils/tourContent so the page stays in step with the editorial
 * content used elsewhere on the site.
 */
import { tourContent } from '$lib/utils/tourContent';

export const LEGENDS_PRODUCT_CODE = 'P8JLJC';

/**
 * Advertised price per person, in AUD.
 *
 * NOTE: the Rezdy product still carries the old $5,000 price, so the booking
 * widget below the fold will quote $5,000 until Rezdy is updated. Update the
 * Rezdy product and this constant together.
 */
export const LEGENDS_PRICE = 3700;

/** Formatted for display, e.g. "3,700". */
export const LEGENDS_PRICE_DISPLAY = LEGENDS_PRICE.toLocaleString('en-AU');

/** Headline facts, shown in the tour page hero and on the golf experiences page. */
export const legendsFacts = [
	{ label: 'Duration', value: '4 days, 4 nights' },
	{ label: 'Golf', value: '4 golf experiences' },
	{ label: 'Journey', value: 'Launceston to Hobart' },
	{ label: 'Group size', value: 'Max 24 travellers' }
];

/** "What's included", shared by the tour page and the golf experiences page. */
export const legendsInclusions = [
	{
		title: '4 nights premium accommodation',
		detail: '2 nights at Country Club Tasmania, Launceston and 2 nights at Wrest Point, Hobart.'
	},
	{
		title: 'Green fees at all four golf experiences',
		detail:
			'Lost Farm, Championship Golf Course, Launceston, Ratho Farm and 7 Mile Beach. Pull buggies included.'
	},
	{
		title: 'Private touring and transfers throughout',
		detail: 'Hosted golf and sightseeing transfers between every course, hotel and stop.'
	},
	{ title: 'Airport transfers', detail: 'On arrival and departure.' },
	{
		title: 'The Range welcome event',
		detail: 'One hour on the range, including pizzas.'
	},
	{
		title: 'Historic and scenic locations',
		detail:
			'Stops at country pubs, colonial villages, Cascade Brewery and options to experience fresh Tasmanian seafood.'
	},
	{
		title: 'Passionate local guide',
		detail: "Bringing Tasmania's history to life — and able to hit a ball."
	},
	{
		title: 'Some light meals included',
		detail:
			'Welcome event - pizza at The Range on arrival (drinks at your expense). Morning tea on 3 golf days. Breakfast at The Country Club - Day 2.'
	},
	{ title: 'Morning Tea at Ratho Farm' },
	{
		title: 'Morning Tea on the go',
		detail:
			'A grab-and-go pack for golf at Barnbougle and 7 Mile Beach, ready for the bag before you tee off.'
	},
	{
		title: 'Legends VIP pack',
		detail: 'Lanyard, ball marker, golf ball and your own Legends Passport'
	},
	{
		title: 'Play for the Legends Trophy',
		detail:
			'Every round counts towards the Legends Trophy, presented at the end of the tour. The winner takes home a $2000 Tassie Tours voucher and some great golf gear.'
	}
];

const content = tourContent.find((t) => t.productCode === LEGENDS_PRODUCT_CODE);

// Itinerary titles are stored as HTML like
// "<p><strong>Thursday - </strong>Arrival in Launceston | Welcome to…</p>".
// Split the weekday off the front so the page can style the two parts apart.
/** @param {string} title */
function parseDay(title) {
	const text = title.replace(/<[^>]+>/g, '').trim();
	const [day, ...rest] = text.split(' - ');
	return { day: day.trim(), heading: rest.join(' - ').trim() };
}

/** The itinerary days, each with its weekday, heading and body HTML. */
export const legendsItinerary = (content?.accordionContent ?? []).map((item) => ({
	...parseDay(item.title),
	content: item.content
}));
